import { useEffect, useState } from 'react';

import { Logo } from '@/components/brand/Logo';
import { Button } from '@/components/ui/button';
import { ApiError, api, type Me } from '@/lib/api';
import { useApi } from '@/lib/useApi';

/**
 * OAuth の同意画面 (`authorization_endpoint`)。
 *
 * **これは SPA の経路で、Worker の経路ではない。** 認可には「誰が許可したのか」
 * が要るので、ログイン状態・二段階認証の関門・停止中/トライアル切れの判定を
 * そのまま効かせたい。Worker 側で HTML を返す作りにすると、その全部を
 * OAuth 用にもう一度書くことになる (`apps/api/src/oauth/api-routes.ts`)。
 *
 * 未ログインのときは `main.tsx` がこの経路にログイン画面を出す。**URL は
 * 変わらない**ので、認可のパラメータ (client_id / PKCE / state) が
 * 消えずに残り、ログインし終えた瞬間にこの画面へ入れ替わる。
 */

type Authorization =
  | {
      status: 'ok';
      clientName: string;
      clientUri: string | null;
      redirectUri: string;
      scope: string;
      resource: string;
    }
  /** client_id / redirect_uri が確かめられていない。**戻り先へ飛ばしてはいけない。** */
  | { status: 'error'; error: string; errorDescription: string }
  /** 要求そのものが不正。戻り先が確かめられているので、クライアントへ返す。 */
  | { status: 'redirect'; redirectTo: string };

export function OAuthConsentPage({ me }: { me: Me }) {
  // クエリはそのまま API に渡す (読み替えの層を挟まない。判断はサーバー側の 1 箇所)
  const params = window.location.search.replace(/^\?/, '');
  const { data, error, loading } = useApi<Authorization>(`/api/oauth/authorization?${params}`);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function decide(approve: boolean) {
    setBusy(true);
    setFailure(null);
    try {
      const result = approve
        ? await api.post<{ redirectTo: string }>('/api/oauth/authorize', { params })
        : { redirectTo: denyRedirect(params) };
      // 戻り先はクライアントの URL なので、履歴に残さず置き換える
      window.location.replace(result.redirectTo);
    } catch (cause) {
      setFailure(cause instanceof ApiError ? cause.message : '通信に失敗しました');
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-6">
      <div className="flex w-full max-w-108 flex-col gap-6 border border-line bg-surface-raised px-8 py-10">
        <Logo height={30} className="text-brand-500" />

        {loading && data === null ? (
          <p className="text-sm text-ink-subtle">{'読み込み中…'}</p>
        ) : null}

        {error ? <p className="text-sm text-alert">{error.message}</p> : null}

        {data?.status === 'error' ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-bold text-alert">{'接続を開始できません'}</p>
            <p className="text-[13px] leading-relaxed text-ink-muted">{data.errorDescription}</p>
            <p className="text-[13px] leading-relaxed text-ink-muted">
              {'この画面から戻り先には飛ばしません。要求元のアプリで設定を確認してください。'}
            </p>
            <span className="font-mono text-xs text-ink-faint">{data.error}</span>
          </div>
        ) : null}

        {data?.status === 'redirect' ? <Bounce to={data.redirectTo} /> : null}

        {data?.status === 'ok' ? (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <span className="text-sm font-bold">
                {data.clientName}
                {' を接続しますか？'}
              </span>
              <p className="text-[13px] leading-relaxed text-ink-muted">
                {
                  'このアプリは、あなたとして工数の記録・確認ができるようになります。記録されるのはあなた自身の工数だけで、他のメンバーの記録や全社の集計には届きません。'
                }
              </p>
            </div>

            <dl className="flex flex-col gap-2 border border-line bg-surface px-4 py-3 text-[13px]">
              <Row label="ログイン中" value={`${me.user.name}（${me.user.email}）`} />
              <Row label="接続先" value={data.resource} mono />
              <Row label="戻り先" value={data.redirectUri} mono />
              {data.clientUri ? <Row label="アプリ" value={data.clientUri} mono /> : null}
            </dl>

            {failure ? <p className="text-sm text-alert">{failure}</p> : null}

            <div className="flex flex-col gap-2 sm:flex-row-reverse">
              <Button
                type="button"
                variant="primary"
                size="md"
                disabled={busy}
                onClick={() => decide(true)}
                className="sm:flex-1"
              >
                {busy ? '処理中…' : '接続を許可'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                disabled={busy}
                onClick={() => decide(false)}
                className="sm:flex-1"
              >
                {'許可しない'}
              </Button>
            </div>

            <p className="text-[13px] leading-relaxed text-ink-subtle">
              {'接続はあとから「MCP接続」の画面でいつでも解除できます。'}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-ink-subtle">{label}</dt>
      <dd className={`break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * 要求そのものが不正だったとき、戻り先へ `error=` を付けて返す。
 *
 * 遷移は effect で行う。描画の途中で `location.replace` を呼ぶと、
 * StrictMode の二重描画で 2 回走る。
 */
function Bounce({ to }: { to: string }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <p className="text-sm text-ink-subtle">{'要求元のアプリに戻っています…'}</p>;
}

/**
 * 「許可しない」を押したときの戻り先。**サーバーには行かない** ——
 * 何も作らないので、記録すべき事実が無い。
 *
 * このボタンが出るのは `status: 'ok'`、つまりサーバーが redirect_uri を
 * 登録済みのものと突き合わせ終えた後だけ。だからクエリの値をそのまま
 * 使ってよい (検証前の値へ飛ばす経路にはならない)。
 */
function denyRedirect(params: string): string {
  const query = new URLSearchParams(params);
  const target = new URL(query.get('redirect_uri') ?? '');
  target.searchParams.set('error', 'access_denied');
  target.searchParams.set('error_description', '利用者が接続を許可しませんでした');
  const state = query.get('state');
  if (state !== null) target.searchParams.set('state', state);
  return target.toString();
}
